import type { ProductAnalytic } from '@prisma/client'
import type { SerializeFrom } from '@remix-run/server-runtime'
import {
  BoxIcon,
  CircleDollarSignIcon,
  EyeIcon,
  HandCoinsIcon,
  PackageIcon,
  ShoppingBagIcon,
} from 'lucide-react'
import { motion } from 'motion/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '~/utils/classnames'
import { centsToEurosFormattedPrice } from '~/utils/number'

type ProductAnalyticsProps = {
  analytics?: SerializeFrom<ProductAnalytic>
  isAnalyticsEnabled?: boolean
  className?: string
}

export const ProductAnalytics = ({
  analytics,
  isAnalyticsEnabled = true,
  className,
  ...props
}: ProductAnalyticsProps) => {
  const { t } = useTranslation()

  const productAnalyticsMetaData = useMemo(
    () => ({
      totalVisits: {
        value: analytics?.totalVisits || 0,
        label: t('page.analytics.clicks'),
        icon: EyeIcon,
      },
      totalInCart: {
        value: analytics?.totalInCart || 0,
        label: t('page.analytics.added_to_cart'),
        icon: ShoppingBagIcon,
      },
      totalSold: {
        value: analytics?.totalSold || 0,
        label: t('page.analytics.sales'),
        icon: PackageIcon,
      },
      totalRevenue: {
        value: centsToEurosFormattedPrice(analytics?.totalRevenue || 0),
        label: t('page.analytics.revenue'),
        icon: HandCoinsIcon,
      },
      totalStock: {
        value: analytics?.totalStock || 0,
        label: t('page.analytics.stock'),
        icon: BoxIcon,
      },
      totalStockValue: {
        value: centsToEurosFormattedPrice(analytics?.totalStockValue || 0),
        label: t('page.analytics.potential_rev'),
        icon: CircleDollarSignIcon,
      },
    }),
    [analytics, t]
  )

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 },
  }

  return (
    <motion.div
      initial="hidden"
      variants={cardVariants}
      animate={isAnalyticsEnabled ? 'visible' : 'hidden'}
      transition={{ duration: 0.15 }}
      className={cn(
        'pointer-events-none origin-bottom-left @max-2xs/media-card:hidden',
        className
      )}
      {...props}
    >
      <div className="bg-foreground text-background w-max min-w-[180px] rounded-md p-3">
        <div className="flex w-full flex-col gap-2">
          {Object.entries(productAnalyticsMetaData).map(
            ([meta, { value, label, icon: IconComponent }]) => (
              <div
                className={cn(
                  'text-2xs flex items-center justify-between gap-2'
                )}
                key={meta}
              >
                <div className="text-background/64 flex items-center gap-1">
                  <IconComponent />
                  <p className="font-medium">{label}</p>
                </div>
                <p className="font-medium tabular-nums">{value}</p>
              </div>
            )
          )}
        </div>
      </div>
    </motion.div>
  )
}
